import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Spinner } from "../ui/spinner";
import { Alert, AlertDescription } from "../ui/alert";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Calendar,
  Filter,
  RefreshCw,
  Search,
  User,
  FileText,
  Shield,
  AlertTriangle,
  Clock,
  ChevronLeft,
  ChevronRight,
  BarChart3,
} from "lucide-react";
import { format } from "date-fns";
import { AuditService } from "../../services/auditService";
import { BusinessUnitService } from "../../services/businessUnitService";

const AuditLogViewer = ({
  organizationId,
  organizationName,
  businessUnitId = null,
  businessUnitName = null,
  onBack,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  // State management
  const [auditLogs, setAuditLogs] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(
    businessUnitId ? "business-unit" : "organization"
  );

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(50);

  // Filters
  const [filters, setFilters] = useState({
    entity_type: "all",
    scope_level: "all",
    business_unit_id: businessUnitId || "all",
    user_id: "",
    field: "",
    since: "",
    until: "",
  });

  // Statistics
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Fetch business units for organization
  const fetchBusinessUnits = async () => {
    try {
      const response =
        await BusinessUnitService.getBusinessUnitsForOrganization(
          organizationId,
          getAccessTokenSilently
        );
      setBusinessUnits(response.businessUnits || []);
    } catch (err) {
      console.error("Error fetching business units:", err);
    }
  };

  // Fetch audit logs
  const fetchAuditLogs = async (page = 1, newFilters = null) => {
    try {
      setLoading(true);
      setError(null);

      const currentFilters = newFilters || filters;
      const params = {
        ...Object.fromEntries(
          Object.entries(currentFilters).filter(
            ([_, value]) => value !== "" && value !== "all"
          )
        ),
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
      };

      let response;
      if (activeTab === "organization") {
        response = await AuditService.getOrganizationAuditHistory(
          organizationId,
          params,
          getAccessTokenSilently
        );
      } else if (
        activeTab === "business-unit" &&
        currentFilters.business_unit_id
      ) {
        response = await AuditService.getBusinessUnitAuditHistory(
          currentFilters.business_unit_id,
          params,
          getAccessTokenSilently
        );
      }

      if (response) {
        setAuditLogs(response.data || []);
        setTotalItems(response.total || 0);
        setCurrentPage(page);
      }
    } catch (err) {
      setError(err.message);
      setAuditLogs([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch audit statistics
  const fetchAuditStats = async () => {
    try {
      setStatsLoading(true);
      const response = await AuditService.getOrganizationAuditStats(
        organizationId,
        {
          since: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        },
        getAccessTokenSilently
      );
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching audit stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setCurrentPage(1);
    fetchAuditLogs(1, newFilters);
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    const newFilters = { ...filters };
    if (tab === "organization") {
      newFilters.business_unit_id = "all";
    }
    setFilters(newFilters);
    fetchAuditLogs(1, newFilters);
  };

  // Clear filters
  const clearFilters = () => {
    const clearedFilters = {
      entity_type: "all",
      scope_level: "all",
      business_unit_id: businessUnitId || "all",
      user_id: "",
      field: "",
      since: "",
      until: "",
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    fetchAuditLogs(1, clearedFilters);
  };

  // Format values for display
  const formatValue = (value) => {
    if (value === null || value === undefined) return "—";
    if (typeof value === "string" && value.length > 100) {
      return `${value.substring(0, 100)}...`;
    }
    return value;
  };

  // Get entity type icon
  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case "Risk":
        return <AlertTriangle className="h-4 w-4" />;
      case "Control":
        return <Shield className="h-4 w-4" />;
      case "User":
        return <User className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  // Get scope level badge variant
  const getScopeBadgeVariant = (scopeLevel) => {
    return scopeLevel === "organization" ? "default" : "secondary";
  };

  useEffect(() => {
    fetchBusinessUnits();
    fetchAuditLogs();
    fetchAuditStats();
  }, [organizationId]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">Audit Logs</h1>
          <p className="text-muted-foreground">
            Change history for{" "}
            {businessUnitId
              ? `${businessUnitName} (${organizationName})`
              : organizationName}
          </p>
        </div>
        <Button variant="outline" onClick={() => fetchAuditLogs(currentPage)}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Total Changes</p>
                  <p className="text-2xl font-bold">
                    {stats.summary?.total_changes || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Active Users</p>
                  <p className="text-2xl font-bold">
                    {stats.summary?.active_users || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Risk Changes</p>
                  <p className="text-2xl font-bold">
                    {stats.by_entity_type?.Risk?.total || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    Control Changes
                  </p>
                  <p className="text-2xl font-bold">
                    {stats.by_entity_type?.Control?.total || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        {!businessUnitId ? (
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="organization">Organization Level</TabsTrigger>
            <TabsTrigger value="business-unit">Business Unit Level</TabsTrigger>
          </TabsList>
        ) : (
          <TabsList className="grid w-full grid-cols-1">
            <TabsTrigger value="business-unit">
              Business Unit Audit History
            </TabsTrigger>
          </TabsList>
        )}

        {/* Filters */}
        <Card className="my-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {/* Entity Type Filter */}
              <div>
                <Label htmlFor="entity-type">Entity Type</Label>
                <Select
                  value={filters.entity_type}
                  onValueChange={(value) =>
                    handleFilterChange("entity_type", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All entities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All entities</SelectItem>
                    <SelectItem value="Risk">Risk</SelectItem>
                    <SelectItem value="Control">Control</SelectItem>
                    <SelectItem value="User">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Scope Level Filter */}
              <div>
                <Label htmlFor="scope-level">Scope Level</Label>
                <Select
                  value={filters.scope_level}
                  onValueChange={(value) =>
                    handleFilterChange("scope_level", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All scopes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All scopes</SelectItem>
                    <SelectItem value="organization">Organization</SelectItem>
                    <SelectItem value="business_unit">Business Unit</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Business Unit Filter */}
              {activeTab === "organization" && (
                <div>
                  <Label htmlFor="business-unit">Business Unit</Label>
                  <Select
                    value={filters.business_unit_id}
                    onValueChange={(value) =>
                      handleFilterChange("business_unit_id", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All business units" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All business units</SelectItem>
                      {businessUnits.map((bu) => (
                        <SelectItem key={bu.id} value={bu.id}>
                          {bu.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Business Unit Selector for Business Unit Tab */}
              {activeTab === "business-unit" && (
                <div>
                  <Label htmlFor="business-unit-select">
                    Select Business Unit
                  </Label>
                  <Select
                    value={filters.business_unit_id}
                    onValueChange={(value) =>
                      handleFilterChange("business_unit_id", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose business unit" />
                    </SelectTrigger>
                    <SelectContent>
                      {businessUnits.map((bu) => (
                        <SelectItem key={bu.id} value={bu.id}>
                          {bu.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Date From */}
              <div>
                <Label htmlFor="date-from">From Date</Label>
                <Input
                  type="date"
                  value={filters.since}
                  onChange={(e) =>
                    handleFilterChange(
                      "since",
                      e.target.value
                        ? new Date(e.target.value).toISOString()
                        : ""
                    )
                  }
                />
              </div>

              {/* Date To */}
              <div>
                <Label htmlFor="date-to">To Date</Label>
                <Input
                  type="date"
                  value={filters.until}
                  onChange={(e) =>
                    handleFilterChange(
                      "until",
                      e.target.value
                        ? new Date(e.target.value).toISOString()
                        : ""
                    )
                  }
                />
              </div>

              {/* Clear Filters Button */}
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {!businessUnitId && (
          <TabsContent value="organization" className="mt-6">
            {/* Organization Level Audit Logs */}
            <Card>
              <CardHeader>
                <CardTitle>Organization Audit History</CardTitle>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Total: {totalItems} entries
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchAuditLogs(currentPage - 1)}
                      disabled={currentPage <= 1 || loading}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchAuditLogs(currentPage + 1)}
                      disabled={currentPage >= totalPages || loading}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="lg" />
                  </div>
                ) : error ? (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                ) : auditLogs.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">
                      No audit logs found.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {auditLogs.map((log) => (
                      <div
                        key={log.id}
                        className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="p-2 bg-muted rounded-md">
                              {getEntityIcon(log.entity_type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge
                                  variant={getScopeBadgeVariant(
                                    log.scope_level
                                  )}
                                >
                                  {log.scope_level}
                                </Badge>
                                <Badge variant="outline">
                                  {log.entity_type}
                                </Badge>
                                <span className="text-sm text-muted-foreground">
                                  {log.field_changed}
                                </span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                <div>
                                  <span className="font-medium">From: </span>
                                  <span className="text-muted-foreground">
                                    {formatValue(log.from_value)}
                                  </span>
                                </div>
                                <div>
                                  <span className="font-medium">To: </span>
                                  <span className="text-muted-foreground">
                                    {formatValue(log.to_value)}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {log.User?.name || "Unknown User"}
                                </div>
                                {log.BusinessUnit && (
                                  <div className="flex items-center gap-1">
                                    <FileText className="h-3 w-3" />
                                    {log.BusinessUnit.name}
                                  </div>
                                )}
                                <div className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {format(
                                    new Date(log.date_changed),
                                    "MMM dd, yyyy HH:mm"
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="business-unit" className="mt-6">
          {/* Business Unit Level Audit Logs */}
          <Card>
            <CardHeader>
              <CardTitle>Business Unit Audit History</CardTitle>
              {!filters.business_unit_id ? (
                <p className="text-sm text-muted-foreground">
                  Please select a business unit to view its audit history.
                </p>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Total: {totalItems} entries
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchAuditLogs(currentPage - 1)}
                      disabled={currentPage <= 1 || loading}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchAuditLogs(currentPage + 1)}
                      disabled={currentPage >= totalPages || loading}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {!filters.business_unit_id ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">
                    Select a business unit from the filters above to view audit
                    logs.
                  </p>
                </div>
              ) : loading ? (
                <div className="flex justify-center py-8">
                  <Spinner size="lg" />
                </div>
              ) : error ? (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">
                    No audit logs found for this business unit.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {auditLogs.map((log) => (
                    <div
                      key={log.id}
                      className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="p-2 bg-muted rounded-md">
                            {getEntityIcon(log.entity_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline">{log.entity_type}</Badge>
                              <span className="text-sm text-muted-foreground">
                                {log.field_changed}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="font-medium">From: </span>
                                <span className="text-muted-foreground">
                                  {formatValue(log.from_value)}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium">To: </span>
                                <span className="text-muted-foreground">
                                  {formatValue(log.to_value)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {log.User?.name || "Unknown User"}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {format(
                                  new Date(log.date_changed),
                                  "MMM dd, yyyy HH:mm"
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuditLogViewer;
